import Cookies from "js-cookie";
import { isDesktopApp } from "./isDesktop";

export const AUTH_SESSION_CLEARED_EVENT = "auth-session-cleared";

export const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const getStoredAccessToken = () =>
  Cookies.get("accessToken") || localStorage.getItem("accessToken") || null;

export const getTokenExpiryTime = (token) => {
  const decoded = parseJwt(token);
  if (!decoded?.exp) return null;
  return decoded.exp * 1000;
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  const expiryMs = getTokenExpiryTime(token);
  if (!expiryMs) return true;
  return Date.now() >= expiryMs;
};

let sessionExpiryTimerId = null;

export const scheduleSessionExpiryCheck = (onExpired) => {
  if (sessionExpiryTimerId) {
    clearTimeout(sessionExpiryTimerId);
    sessionExpiryTimerId = null;
  }

  const token = getStoredAccessToken();
  if (!token) {
    return () => undefined;
  }

  const expiryMs = getTokenExpiryTime(token);
  if (!expiryMs) {
    return () => undefined;
  }

  const delay = expiryMs - Date.now();
  if (delay <= 0) {
    onExpired();
    return () => undefined;
  }

  sessionExpiryTimerId = setTimeout(onExpired, delay);
  return () => {
    if (sessionExpiryTimerId) {
      clearTimeout(sessionExpiryTimerId);
      sessionExpiryTimerId = null;
    }
  };
};

const ensureAuthStorageSync = (token) => {
  if (!token) return;

  if (localStorage.getItem("accessToken") !== token) {
    localStorage.setItem("accessToken", token);
  }

  if (!Cookies.get("accessToken")) {
    const decoded = parseJwt(token);
    const expires = decoded?.exp ? new Date(decoded.exp * 1000) : undefined;
    Cookies.set("accessToken", token, expires ? { expires } : undefined);
  }
};

export const clearAuthSession = () => {
  const cookies = Cookies.get();
  for (const cookie in cookies) {
    Cookies.remove(cookie);
  }

  sessionStorage.clear();

  [
    "userDetails",
    "accessToken",
    "activeTab",
    "selectedOrgName",
    "selectedOrgId",
    "userPermissions",
    "selectedFields",
    "FCMToken",
    "RcselectedOrgId",
    "RcselectedOrgName",
    "departmentId",
    "projectId",
    "activityId",
    "selectedActivity",
    "formdepartmentId",
    "isLoggedIn",
  ].forEach((key) => localStorage.removeItem(key));

  window.dispatchEvent(new Event(AUTH_SESSION_CLEARED_EVENT));
};

export const validateSession = () => {
  const token = getStoredAccessToken();

  if (!token || isTokenExpired(token)) {
    if (
      token ||
      localStorage.getItem("accessToken") ||
      Cookies.get("accessToken") ||
      localStorage.getItem("isLoggedIn")
    ) {
      clearAuthSession();
    }
    return false;
  }

  ensureAuthStorageSync(token);
  return true;
};

export const isAuthenticated = () => validateSession();

export const redirectToLogin = () => {
  clearAuthSession();
  if (isDesktopApp()) {
    window.location.hash = "#/";
    if (window.location.hash !== "#/") {
      window.location.hash = "/";
    }
    return;
  }
  if (window.location.pathname !== "/") {
    window.location.assign("/");
  }
};

export const isSessionValid = () => {
  const token = getStoredAccessToken();
  if (!token || isTokenExpired(token)) {
    return false;
  }
  ensureAuthStorageSync(token);
  return true;
};

export const assertValidSessionOrRedirect = () => {
  if (isSessionValid()) {
    return true;
  }
  redirectToLogin();
  return false;
};

const AUTH_FAILURE_MESSAGE = /session expired|authentication required/i;
const ACCESS_DENIED_MESSAGE = /access denied|insufficient permissions|organization context required/i;

export const isPermissionDeniedError = (error) => {
  const status = error?.response?.status ?? error?.httpStatus;
  const apiCode = error?.response?.data?.header?.code ?? error?.responseCode;
  const envelopeMessage = error?.response?.data?.body?.value ?? error?.envelope?.body?.value;

  if (apiCode === 608 || status === 403) {
    return true;
  }
  return typeof envelopeMessage === "string" && ACCESS_DENIED_MESSAGE.test(envelopeMessage);
};

export const isAuthFailureError = (error) => {
  if (isPermissionDeniedError(error)) {
    return false;
  }

  const status = error?.response?.status ?? error?.httpStatus;
  const apiCode = error?.response?.data?.header?.code ?? error?.responseCode;
  const envelopeMessage = error?.response?.data?.body?.value ?? error?.envelope?.body?.value;

  if (status === 401) {
    if (typeof envelopeMessage === "string" && ACCESS_DENIED_MESSAGE.test(envelopeMessage)) {
      return false;
    }
    return true;
  }

  if (typeof envelopeMessage === "string" && AUTH_FAILURE_MESSAGE.test(envelopeMessage)) {
    return true;
  }

  if (typeof error?.message === "string" && AUTH_FAILURE_MESSAGE.test(error.message)) {
    return true;
  }

  if (apiCode === 601 && typeof envelopeMessage === "string" && AUTH_FAILURE_MESSAGE.test(envelopeMessage)) {
    return true;
  }

  return false;
};

export const getApiErrorMessage = (error, fallbackMessage = "Request failed") => {
  if (!error) return fallbackMessage;
  if (typeof error.message === "string" && error.message.trim()) {
    return error.message;
  }
  const envelopeMessage = error?.response?.data?.body?.value ?? error?.envelope?.body?.value;
  if (typeof envelopeMessage === "string" && envelopeMessage.trim()) {
    return envelopeMessage;
  }
  return fallbackMessage;
};
